import React, { useEffect, useState, useCallback } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, List, ListItemButton, ListItemText, Chip, CircularProgress, Box,
} from '@mui/material'
import { toast } from 'react-toastify'
import { PatientService } from '../../../../services/patient.service'
import Helper from '../../../../utils/helper'

/**
 * Shared picker for bespoke symptom tracking (tox ∪ *__attr questionnaires).
 *
 * Used by the à-la-carte "+ track a symptom" affordance on the Toxicity group. The
 * SAME endpoint/flow also backs the experiment-plan readout add. Items already
 * surfaced for the patient (already collectible — passed in via `alreadySurfaced`,
 * computed client-side from the patient's current calculator set, Option A) are
 * greyed + disabled, so the provider only adds NEW bespoke symptoms.
 *
 * On add: POST /patients/<ref>/tracked-questionnaires → the backend marks a tracked
 * QuesScoreCalc → it surfaces under Toxicity → clicking it opens the same answer
 * dialog → once answered it charts on the Trajectory (PROM path).
 */
interface TrackableItem {
  name: string
  display_name: string
  category: string
}

interface IProps {
  open: boolean
  onClose: () => void
  patientRefId: string
  /** Questionnaire KB names already surfaced for this patient (greyed + disabled). */
  alreadySurfaced: Set<string>
  /** Called after a successful add so the host can refresh the patient details. */
  onAdded: (questionnaireName: string) => void
}

const patientService = new PatientService()

const TrackSymptomPickerComponent: React.FC<IProps> = ({
  open, onClose, patientRefId, alreadySurfaced, onAdded,
}) => {
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<TrackableItem[]>([])
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState<string | null>(null)

  const load = useCallback((term: string) => {
    if (!patientRefId) return
    setLoading(true)
    patientService.getTrackablePicker(patientRefId, term)
      .then((res: any) => setItems(res.data || []))
      .catch((err: any) => { console.error(err); setItems([]) })
      .finally(() => setLoading(false))
  }, [patientRefId])

  // Debounced search — the catalog is ~2000 items, so this is a type-ahead.
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => load(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search, open, load])

  const handleAdd = (item: TrackableItem) => {
    if (alreadySurfaced.has(item.name) || adding) return
    setAdding(item.name)
    patientService.addTrackedQuestionnaire(patientRefId, item.name)
      .then(() => {
        toast.success(`Tracking ${item.display_name}`, Helper.bottom_center())
        onAdded(item.name)
      })
      .catch((err: any) => {
        console.error(err)
        toast.error(err?.response?.data?.error?.message || 'Could not track this symptom', Helper.bottom_center())
      })
      .finally(() => setAdding(null))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Track a symptom</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth size="small" variant="outlined" sx={{ mt: 1, mb: 1 }}
          placeholder="Search symptoms (e.g. muscle pain, dyspnea, edema)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Box sx={{ minHeight: 240, maxHeight: 360, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
          ) : items.length === 0 ? (
            <Box sx={{ color: 'text.secondary', textAlign: 'center', py: 4, fontSize: 13 }}>
              {search ? 'No matching symptoms.' : 'Type to search symptoms.'}
            </Box>
          ) : (
            <List dense disablePadding>
              {items.map((item) => {
                const surfaced = alreadySurfaced.has(item.name)
                return (
                  <ListItemButton
                    key={item.name}
                    disabled={surfaced || adding === item.name}
                    onClick={() => handleAdd(item)}
                  >
                    <ListItemText
                      primary={item.display_name}
                      secondary={item.category === 'SYMPTOM_EVAL' ? 'symptom' : 'toxicity'}
                      primaryTypographyProps={{ fontSize: 13.5 }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                    />
                    {surfaced && <Chip label="already tracked" size="small" variant="outlined" />}
                    {adding === item.name && <CircularProgress size={16} />}
                  </ListItemButton>
                )
              })}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default TrackSymptomPickerComponent
